import { useEffect, useState, useCallback } from "react";
import {
  Card,
  CardHeader,
  CardFooter,
  Button,
  Input,
  Row,
  Col,
  Spinner,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import Pagination from "components/shared/Pagination";
import TableComponent from "components/shared/Table";
import tick from "assets/img/tick.png";
import cross from "assets/img/cros.png";
import AddTable from "components/Modals/AddTable";
import { fetchTables, occupiedChange } from "store/actions/staffAction";

function Table() {
  const dispatch = useDispatch();
  const { user, uid } = useSelector((state) => state.auth);
  const { tablesData, isLoading, isUpdateLoading } = useSelector(
    (state) => state.staff
  );

  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [addModal, setAddModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [occupiedStatus, setOccupiedStatus] = useState({});

  const formatTimestamp = (timestamp) => {
    if (!timestamp || !timestamp.seconds) return "-";
    const date = new Date(timestamp.seconds * 1000);
    const formattedTime = date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    return `${formattedTime}`;
  };

  const addToggle = () => setAddModal((prev) => !prev);

  const handleChange = (e) => setSearchTerm(e.target.value);

  const filteredTables = Object.entries(tablesData?.tables || {})
    .map(([tableKey, table]) => ({ tableKey, ...table }))
    .filter(({ tableNo, customerName }) => {
      const searchValue = searchTerm.toLowerCase();
      return (
        (tableNo?.toString().includes(searchValue) ?? false) ||
        (customerName?.toLowerCase().includes(searchValue) ?? false)
      );
    });
  // .sort((a, b) => a.tableNo - b.tableNo);

  const paginatedTables = filteredTables.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const updateOccupiedStatus = useCallback(() => {
    const now = new Date();
    const status = {};

    Object.entries(tablesData?.tables || {}).forEach(([key, table]) => {
      const { occupiedAt, expireAt } = table;
      if (!occupiedAt || !expireAt) {
        status[key] = false;
      } else {
        status[key] =
          now >= new Date(occupiedAt.seconds * 1000) &&
          now <= new Date(expireAt.seconds * 1000);
      }
    });
    setOccupiedStatus(status);
  }, [tablesData]);

  useEffect(() => {
    setIsInitialLoading(true);
    dispatch(fetchTables(uid));
    setTimeout(() => {
      setIsInitialLoading(false);
    }, 3000);
  }, [dispatch, uid]);

  useEffect(() => {
    updateOccupiedStatus();
    const interval = setInterval(updateOccupiedStatus, 60000);
    return () => clearInterval(interval);
  }, [updateOccupiedStatus]);

  const handleOccupiedChange = (tableKey) => {
    dispatch(occupiedChange(user?.restaurantID, tableKey));
  };

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  return (
    <Row>
      <div className="col">
        <Card className="shadow">
          <CardHeader className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center px-2 px-lg-4">
            <Col xs="12" lg="3" xl="6" className="mb-3 mb-lg-0">
              <h3 className="mb-0">{`Tables (${
                tablesData?.info?.totalTables || 0
              })`}</h3>
            </Col>
            <Row className="w-100 mx-0">
              <Col xs="12" sm="4" lg="4" className="mb-3 mb-sm-0">
                <Input
                  placeholder="Search here...."
                  value={searchTerm}
                  onChange={handleChange}
                />
              </Col>
              <Col xs="12" sm="4" lg="4" className="mb-3 mb-sm-0">
                <Input
                  type="select"
                  value={pageSize}
                  onChange={handlePageSizeChange}
                >
                  <option value="5">Show Entries</option>
                  <option value="10">10</option>
                  <option value="15">15</option>
                  <option value="20">20</option>
                </Input>
              </Col>
              <Col xs="12" sm="4" lg="4">
                <Button color="primary" onClick={addToggle} block>
                  {tablesData?.info?.totalTables > 0 ? "Manage" : "Add"}
                </Button>
              </Col>
            </Row>
          </CardHeader>

          <TableComponent responsive>
            <thead className="thead-light">
              <tr>
                <th scope="col">Table No</th>
                <th scope="col">Customer Name</th>
                <th scope="col">Reservation</th>
                <th scope="col">Occupied</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading || isInitialLoading ? (
                <tr>
                  <td colSpan="5" className="text-center py-5">
                    <Spinner size="lg" color="primary" />
                  </td>
                </tr>
              ) : paginatedTables.length > 0 ? (
                paginatedTables.map(
                  ({
                    tableKey,
                    tableNo,
                    customerName,
                    occupiedAt,
                    expireAt,
                  }) => {
                    const currentlyOccupied = occupiedStatus[tableKey];

                    return (
                      <tr key={tableKey}>
                        <td>{tableNo}</td>
                        <td>
                          {currentlyOccupied ? customerName : "No customer"}
                        </td>
                        <td>
                          {currentlyOccupied
                            ? `${formatTimestamp(
                                occupiedAt
                              )} - ${formatTimestamp(expireAt)}`
                            : "-"}
                        </td>
                        <td>
                          <img
                            src={currentlyOccupied ? tick : cross}
                            alt="Occupy"
                            style={{ height: "35px", width: "35px" }}
                          />
                        </td>
                        <td>
                          <Button
                            className="btn-sm"
                            color="danger"
                            onClick={() => handleOccupiedChange(tableKey)}
                            disabled={
                              isUpdateLoading[tableKey] || !currentlyOccupied
                            }
                          >
                            Unoccupy
                            {isUpdateLoading[tableKey] && (
                              <Spinner
                                size="sm"
                                color="white"
                                className="ml-2"
                              />
                            )}
                          </Button>
                        </td>
                      </tr>
                    );
                  }
                )
              ) : (
                <tr>
                  <td colSpan="5" className="text-center py-5">
                    No tables reserved at that moment.
                  </td>
                </tr>
              )}
            </tbody>
          </TableComponent>
          <CardFooter className="py-4">
            {Math.ceil(filteredTables.length / pageSize) > 1 && (
              <Pagination
                currentPage={currentPage - 1}
                totalPages={Math.ceil(filteredTables.length / pageSize)}
                onPageChange={(page) => setCurrentPage(page + 1)}
              />
            )}
          </CardFooter>
        </Card>
      </div>

      <AddTable isOpen={addModal} toggle={addToggle} />
    </Row>
  );
}

export default Table;
